import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Flag, FolderKanban, Trash2, User, Layers, Clock } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import { api, TASK_STATUSES, PRIORITIES, PHASES, fmtDate } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Slider } from "../ui/slider";
import { StatusPill, PriorityPill } from "./bits";
import Chat from "./Chat";
import { MemberItems } from "./MemberSelect";
import { StatusActionBar } from "./StatusActions";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-500">
      <Icon className="h-3.5 w-3.5" /> {label}
    </p>
    {children}
  </div>
);

export default function TaskSheet() {
  const { taskId, closeTask, tasks, team, projects, updateTask, refresh } = useApp();
  const nav = useNavigate();
  const task = tasks.find((t) => t.id === taskId);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (task) setForm({ title: task.title, description: task.description || "", notes: task.notes || "", startDate: task.startDate || "", dueDate: task.dueDate || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, task?.title, task?.description, task?.notes, task?.startDate, task?.dueDate]);

  const save = (patch) => updateTask(task.id, patch);
  const saveText = (key) => {
    if (form[key] !== (task[key] || "")) save({ [key]: form[key] });
  };
  const remove = async () => {
    await api.delete(`/tasks/${task.id}`);
    toast.success("Task deleted");
    closeTask();
    refresh();
  };

  return (
    <Sheet open={!!taskId} onOpenChange={(o) => !o && closeTask()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="task-sheet">
        {task && form ? (
          <>
            <SheetHeader className="text-left">
              <p className="text-xs font-semibold text-[#a08450]">TASK-{task.id}{task.milestone ? " · Milestone" : ""}</p>
              <SheetTitle className="sr-only">{task.title}</SheetTitle>
              <SheetDescription className="sr-only">Task details</SheetDescription>
              <Input
                data-testid="task-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                onBlur={() => form.title.trim() && saveText("title")}
                className="h-auto border-transparent px-0 text-xl font-bold text-slate-900 shadow-none hover:border-slate-200 focus-visible:px-2"
              />
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <StatusPill status={task.status} />
                <PriorityPill priority={task.priority} />
                {task.overdue && <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">Overdue</span>}
              </div>
              <StatusActionBar task={task} />
            </SheetHeader>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <Field icon={Flag} label="Status">
                <Select value={task.status} onValueChange={(v) => save({ status: v })}>
                  <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field icon={Flag} label="Priority">
                <Select value={task.priority} onValueChange={(v) => save({ priority: v })}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field icon={User} label="Assigned to">
                <Select value={task.assigneeId || "none"} onValueChange={(v) => save({ assigneeId: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="task-assignee-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    <MemberItems team={team} />
                  </SelectContent>
                </Select>
              </Field>
              <Field icon={Layers} label="Project phase">
                <Select value={task.phase || "none"} onValueChange={(v) => save({ phase: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {PHASES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field icon={Calendar} label="Start">
                <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} onBlur={() => saveText("startDate")} />
              </Field>
              <Field icon={Clock} label="Projected finish">
                <Input type="date" data-testid="task-due-input" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} onBlur={() => saveText("dueDate")} />
              </Field>
              <div className="col-span-2">
                <Field icon={FolderKanban} label="Project">
                  <button
                    onClick={() => { closeTask(); nav(`/projects/${task.projectId}`); }}
                    className="text-sm font-semibold text-[#00573f] hover:underline"
                  >
                    {projects.find((p) => p.id === task.projectId)?.name || task.project}
                  </button>
                </Field>
              </div>
              <div className="col-span-2">
                <p className="mb-2 flex items-center justify-between text-xs font-medium text-slate-500">
                  <span>% Complete</span>
                  <span className="font-bold text-slate-800">{task.percentComplete || 0}%</span>
                </p>
                <Slider
                  data-testid="task-progress-slider"
                  value={[task.percentComplete || 0]}
                  step={10}
                  max={100}
                  onValueCommit={(v) => save({ percentComplete: v[0], ...(v[0] === 100 ? { status: "Completed" } : v[0] > 0 && task.status === "Not Started" ? { status: "In-Progress" } : {}) })}
                />
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <p className="mb-1.5 text-xs font-medium text-slate-500">Description</p>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} onBlur={() => saveText("description")} placeholder="Add a description..." />
              </div>
              <div>
                <p className="mb-1.5 text-xs font-medium text-slate-500">History / Notes / Issues</p>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} onBlur={() => saveText("notes")} placeholder="Add notes..." />
              </div>
              {task.dateComplete && <p className="text-xs text-emerald-700">Completed on {fmtDate(task.dateComplete)}</p>}
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 p-5">
              <Chat scope="task" refId={task.id} title="Task Comments" height="h-48" />
            </div>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700" data-testid="task-delete-btn">
                  <Trash2 className="h-4 w-4" /> Delete task
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this task?</AlertDialogTitle>
                  <AlertDialogDescription>“{task.title}” and its comments will be permanently removed.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : (
          <p className="p-6 text-sm text-slate-400">Loading task...</p>
        )}
      </SheetContent>
    </Sheet>
  );
}
